/**
 * DeviceTypeCard - Shows proposed device configuration during awaiting_confirmation phase
 */

import { Cpu, Radio, Code, Headphones, Waves } from 'lucide-react';
import type { DiscoveredDeviceType } from '../types';

interface DeviceTypeCardProps {
  discovered: DiscoveredDeviceType;
  onConfirm: () => void;
  onReject: () => void;
  isProcessing?: boolean;
}

const protocolIcons: Record<string, typeof Cpu> = {
  lorawan: Radio,
  gprs: Waves,
  nb_iot: Waves,
  wifi: Waves,
  ble: Waves,
  zigbee: Radio,
};

export default function DeviceTypeCard({ discovered, onConfirm, onReject, isProcessing }: DeviceTypeCardProps) {
  const ProtoIcon = protocolIcons[discovered.communication_protocol || ''] || Waves;
  const discoverySource = discovered.discovery_source || 'fallback';
  const discoveryPillClass =
    discoverySource === 'web'
      ? 'bg-emerald-100 text-emerald-800'
      : 'bg-amber-100 text-amber-800';
  const discoveryLabel = discoverySource === 'web' ? 'Web Discovered' : 'Fallback Profile';

  return (
    <div className="rounded-xl border border-blue-200 bg-gradient-to-br from-white to-blue-50/40 p-5 shadow-sm space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900">
            {discovered.manufacturer} {discovered.model}
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {discovered.device_category?.replace(/_/g, ' ')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 text-xs font-semibold rounded-full ${discoveryPillClass}`} title={discovered.fallback_reason || ''}>
            {discoveryLabel}
          </span>
          <span className="px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
            Proposed
          </span>
        </div>
      </div>

      {/* Protocol & Format */}
      <div className="grid grid-cols-3 gap-3">
        <div className="flex items-center gap-2 rounded-lg border border-gray-100 bg-white/70 p-3">
          <ProtoIcon className="w-4 h-4 text-blue-600" />
          <div>
            <div className="text-xs text-gray-500">Protocol</div>
            <div className="text-sm font-medium text-gray-900">
              {discovered.communication_protocol?.replace(/_/g, ' ').toUpperCase() || 'Unknown'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-gray-100 bg-white/70 p-3">
          <Code className="w-4 h-4 text-purple-600" />
          <div>
            <div className="text-xs text-gray-500">Payload</div>
            <div className="text-sm font-medium text-gray-900">
              {discovered.payload_format?.toUpperCase() || 'Unknown'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-gray-100 bg-white/70 p-3">
          <Headphones className="w-4 h-4 text-teal-600" />
          <div>
            <div className="text-xs text-gray-500">Listener</div>
            <div className="text-sm font-medium text-gray-900">
              {discovered.listener_mode?.replace(/_/g, ' ') || 'HTTP'}
            </div>
          </div>
        </div>
      </div>

      {/* Telemetry fields */}
      {discovered.telemetry_fields && discovered.telemetry_fields.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-gray-700 mb-2">Telemetry Fields</div>
          <div className="flex flex-wrap gap-1.5">
            {discovered.telemetry_fields.map((f, i) => (
              <span
                key={i}
                className="inline-flex items-center px-2.5 py-1 rounded-md text-xs bg-gray-100 text-gray-700 border border-gray-200"
                title={f.description}
              >
                {f.name}
                {f.unit && <span className="ml-1 text-gray-400">({f.unit})</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Summary */}
      {discovered.summary && (
        <p className="text-sm text-gray-600 leading-relaxed">{discovered.summary}</p>
      )}

      {discovered.datasheet_url && (
        <div className="text-xs">
          <a
            href={discovered.datasheet_url}
            target="_blank"
            rel="noreferrer"
            className="text-blue-700 hover:text-blue-800 underline"
          >
            View datasheet source
          </a>
        </div>
      )}

      {/* Sample payload */}
      {discovered.sample_payload && (
        <details className="text-xs">
          <summary className="cursor-pointer text-gray-500 hover:text-gray-700">Sample payload</summary>
          <pre className="mt-2 p-3 bg-gray-900 text-gray-100 rounded-lg overflow-x-auto text-xs">
            {JSON.stringify(discovered.sample_payload, null, 2)}
          </pre>
        </details>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={onConfirm}
          disabled={isProcessing}
          className="flex-1 px-4 py-2.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 transition-colors"
        >
          {isProcessing ? 'Processing...' : 'Looks good - proceed'}
        </button>
        <button
          onClick={onReject}
          disabled={isProcessing}
          className="px-4 py-2.5 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          Try again
        </button>
      </div>
    </div>
  );
}
